import {
  buildPath,
  buildTemplatePath,
  Grid,
  Page,
  PageRoute,
  useParams,
  Stack,
  GridBreak,
  Box,
  newToast,
  buildOptionalParams,
  Typography,
  Button,
} from "@compass/core";
import {
  Form,
  FormResponse,
  FormQuestionResponse,
  PublicSurveyPageDto,
  PublicSurveyResponseDto,
  PublicSurveyViewResource,
  FormQuestion,
  FormSection,
  PublicSurveySectionDto,
} from "generated/openApi";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import ApiService from "services/apiService";
import { PublicSurveyTitle } from "./PublicSurveyTitle";
import { PublicSurveyQuestionPage } from "./PublicSurveyQuestionPage";
import { createEntity } from "utils/CreateEntity";
import produce from "immer";
import { setAppToast } from "redux/reducers/appSlice";
import { PublicSurveySubmission } from "./PublicSurveySubmission";
import {
  TransientFormAnswer,
  TransientQuestionResponse,
  responseHasAnswer,
} from "../common/TransientQuestionResponse";
import { FloatingHeader } from "../common/FloatingHeader";
import { useReactToPrint } from "react-to-print";

const routeName = "survey";

class Params {
  id: string = "";
  client: string = "demo";
}

class OptionalParams {
  surveyResponse?: string = "";
  isViewMode?: boolean = false;
}

export const publicSurveyPageRoute: PageRoute<Params, OptionalParams> = {
  handle: "survey",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
  getOptionalParams: (params) => buildOptionalParams(params),
};

export const PublicSurveyPage = () => {
  const { params, optionalParams } = useParams(routeName, Params, OptionalParams);

  const dispatch = useDispatch();

  const [validationErrors, setValidationErrors] = useState<object>();
  const [transientQuestionResponses, setTransientQuestionResponses] = useState<TransientQuestionResponse[]>([]);
  const [viewResource, setViewResource] = useState<PublicSurveyViewResource>({} as PublicSurveyViewResource);
  const [survey, setSurvey] = useState<Form>();
  const [surveyPages, setSurveyPages] = useState<PublicSurveyPageDto[]>();
  const [currentPage, setCurrentPage] = useState<PublicSurveyPageDto>();
  const [showSubmittionPage, setShowSubmissionPage] = useState<boolean>();
  const [pageIndex, setPageIndex] = useState<number>(1);
  const [maxPageIndex, setMaxPageIndex] = useState<number>(1);
  const [surveyResponse, setSurveyResponse] = useState<FormResponse>();
  const [firstErroredQuestion, setFirstErroredQuestion] = useState<string>();
  const [centerLogoUrl, setCenterLogoUrl] = useState<string>();
  const [leftLogoUrl, setLeftLogoUrl] = useState<string>();
  const [rightLogoUrl, setRightLogoUrl] = useState<string>();

  // for branching v2
  const [hasBranches, setHasBranches] = useState<boolean>(false);
  const [activeQuestions, setActiveQuestions] = useState<FormQuestion[]>([]);
  const [activeSections, setActiveSections] = useState<FormSection[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const submissionInProgressRef = useRef<boolean>(false);

  // for printing/downloading file
  const [isPrinting, setIsPrinting] = useState<boolean>(false);
  const contentRef = useRef(null);
  const reactToPrintFn = useReactToPrint({ contentRef });

  useEffect(() => {
    getSurvey();
    fetchViewResource();
  }, []);

  useEffect(() => {
    if (!currentPage) {
      if (optionalParams.isViewMode && optionalParams.surveyResponse && surveyPages?.length) {
        ApiService.publicSurvey.getSurveyResponse(optionalParams.surveyResponse, params.client).then((response) => {
          setSurveyResponse(response);
          if (hasBranches) {
            initializeSurveyQuestions();
          }
          else {
            setActiveQuestions(survey?.formQuestions!);
            setCurrentPage(surveyPages![0]);
          }
        });
      } else if (surveyPages?.length) {
        if (hasBranches) {
          initializeSurveyQuestions();
        }
        else {
          setActiveQuestions(survey?.formQuestions!);
          setCurrentPage(surveyPages![0]);
        }
      }
    }
  }, [surveyPages]);

  useEffect(() => {
    if (hasBranches) {
      initializeSurveyQuestions();
    }
  }, [transientQuestionResponses]);

  const initializeSurveyQuestions = () => {
    if (!hasBranches) {

    }
    else {
      // set up initial active questions (i.e questions with no branchedFromFormAnswer or branchedFromFormQuestion)
      var hasNext: boolean = true;
      var activeQuestions: FormQuestion[] = [];
      var activeSections: FormSection[] = [];

      // set up initial
      survey?.formQuestions?.forEach((q) => {
        if (!questionIsBranched(q)) {
          activeQuestions.push(q);
        }
      });
      survey?.formSections?.forEach((s) => {
        if (!sectionIsBranched(s)) {
          activeSections.push(s);
        }
      });

      while (hasNext) {
        // handle questions first
        var nextActiveQuestions = findActiveBranchedQuestions(activeQuestions);

        // if no new active questions
        if (nextActiveQuestions == activeQuestions) {
          hasNext = false;
        }
        else {
          // otherwise mark them as active
          if (nextActiveQuestions != activeQuestions) {
            activeQuestions = activeQuestions.concat(nextActiveQuestions);
          }
        }

        const temp: FormQuestion[] = [];
        activeQuestions.map((n) => {
          const parentQuestionIds = findParentQuestionIds(n);
          var branchIndex = temp.findIndex((t) => parentQuestionIds.some((p) => p == t.id));
          if (branchIndex != - 1) {
            temp.splice(branchIndex + 1, 0, n);
          }
          else {
            temp.push(n);
          }
        })
        activeQuestions = temp;

        // handle sections after we find active questions
        if (survey?.formSections && activeQuestions.some(q => q.branchToFormSectionId != null)) {
          activeSections = survey?.formSections?.filter(s => activeQuestions.some(q => q.branchToFormSectionId == s.id));
        }
      }

      setActiveQuestions(activeQuestions);
      setActiveSections(activeSections);

      // set up pages
      let pageCount: number = 1;

      let sectionCount: number = 0;

      let activePages: PublicSurveyPageDto[] = [];
      let startingPage: PublicSurveyPageDto = createBlankPage(pageCount);
      activePages.push(startingPage);

      let tempCurrentPage = startingPage;
      activeQuestions.forEach((q) => {
        if (!q.startNewPage) {
          tempCurrentPage.sections![sectionCount].questions?.push(q);

          if (q.branchToFormSectionId) {
            var mappedSection = survey?.formSections?.find((f) => f.id == q.branchToFormSectionId);

            let newSection: PublicSurveySectionDto = {
              section: mappedSection!,
              questions: []
            };
            tempCurrentPage.sections?.push(newSection);
            sectionCount++;
          }
        }
        else {
          pageCount = pageCount + 1;
          sectionCount = 0;
          var nextPage = createBlankPage(pageCount);
          tempCurrentPage = nextPage;
          tempCurrentPage.sections![sectionCount].questions?.push(q);
          activePages.push(tempCurrentPage);
        }
      })

      let findCurrentPage = activePages.find(p => p.pageNumber == pageIndex);

      if (JSON.stringify(findCurrentPage) != JSON.stringify(currentPage)) {
        setCurrentPage(findCurrentPage!);
      }
      if (JSON.stringify(activePages) != JSON.stringify(surveyPages)) {
        setSurveyPages(activePages);
      }
      setMaxPageIndex(pageCount);
    }
  }

  const createBlankPage = (pageNumber: number): PublicSurveyPageDto => {
    let newPage: any = {
      pageNumber,
      sections: [
        {
          questions: [],
        }
      ],
    }
    return newPage;
  }


  const findActiveBranchedQuestions = (activeQuestions: FormQuestion[]): FormQuestion[] => {
    var nextActiveQuestions: FormQuestion[] = [];
    var activeQuestionIds = activeQuestions.flatMap(q => q.id);
    var activeFormAnswerIds: number[] = [];

    transientQuestionResponses.forEach((t) => {
      var answers = t.formAnswers.flatMap(x => x.formAnswerId);
      var newAnswers = answers.filter(a => a && !activeFormAnswerIds.includes(a));
      if (newAnswers.length > 0) {
        activeFormAnswerIds = activeFormAnswerIds.concat(newAnswers as number[]);
      }
    })

    var branchingQuestions = survey?.formQuestions?.filter(q => questionIsBranched(q));

    if (!branchingQuestions || branchingQuestions.length == 0) {
      return activeQuestions;
    }

    branchingQuestions.forEach((q) => {
      // check for branching question
      if (q.branchedFromFormQuestionIds?.some(b => activeQuestionIds.includes(b))) {
        if (!activeQuestionIds.includes(q.id)) {
          nextActiveQuestions.push(q);
        }
      }

      // check for branching answers
      if (q.branchedFromFormAnswerIds != null && q.branchedFromFormAnswerIds.length > 0) {
        if (activeFormAnswerIds.some(a => q.branchedFromFormAnswerIds?.some(b => b.toString() == a.toString()))) {
          if (!activeQuestionIds.includes(q.id)) {
            nextActiveQuestions.push(q);
          }
        }
      }
    })

    if (nextActiveQuestions.length > 0) {
      return nextActiveQuestions;
    }

    return activeQuestions;
  }

  const responseIsActive = (response: TransientQuestionResponse): boolean => {
    var responseQuestion = findResponseQuestion(response);
    if (!questionIsBranched(responseQuestion)) {
      return true;
    }
    else {
      if (activeQuestions.some(q => q.id == responseQuestion.id)) {
        return true;
      }
    }
    return false;
  }

  const findResponseQuestion = (response: TransientQuestionResponse): FormQuestion => {
    var matchedQuestion = survey?.formQuestions?.find(q => q.id == response.questionId);
    return matchedQuestion!;
  }

  const findParentQuestionIds = (question: FormQuestion): number[] => {
    const parentQuestionIds: number[] = [];
    if (question.branchedFromFormQuestionIds && question.branchedFromFormQuestionIds.length > 0) {
      question.branchedFromFormQuestionIds!.map((q) => {
        parentQuestionIds.push(q);
      })
    }
    if (question.branchedFromFormAnswerIds && question.branchedFromFormAnswerIds.length > 0) {
      question.branchedFromFormAnswerIds!.map((a) => {
        const formQuestion = survey?.formQuestions?.find(q => q.formAnswers?.some((fa) => fa.id == a));
        parentQuestionIds.push(formQuestion!.id);
      })
    }
    return parentQuestionIds;
  }

  const questionIsBranched = (question: FormQuestion): boolean => {
    if ((question.branchedFromFormAnswerIds != null && question.branchedFromFormAnswerIds.length > 0) ||
      (question.branchedFromFormQuestionIds != null && question.branchedFromFormQuestionIds.length > 0) ||
      (question.branchedFromFormSectionIds != null && question.branchedFromFormSectionIds.length > 0))
      return true;
    else return false;
  }

  const sectionIsBranched = (section: FormSection): boolean => {
    if ((section.branchedFromFormQuestionIds != null && section.branchedFromFormQuestionIds.length > 0))
      return true;
    else return false;
  }

  useEffect(() => {
    if (surveyResponse) {
      const temp: TransientQuestionResponse[] = [];

      survey?.formQuestions?.map((q) => {
        temp.push({
          questionId: q.id,
          writeIn: q.defaultAnswer || null,
          writeInDate: null,
          writeInDecimal: null,
          formAnswers: [],
          signatureFile: null,
          fileUpload: null,
        });
      });

      surveyResponse.formQuestionResponses?.forEach((r) => {
        var matchedResponseIndex = temp.findIndex((x) => x.questionId == r.formQuestionId);
        if (matchedResponseIndex != -1) {
          temp[matchedResponseIndex] = {
            ...temp[matchedResponseIndex],
            writeIn: r.writeIn || null,
            writeInDate: r.writeInDate ? new Date(r.writeInDate).toISOString().split('T')[0] : null,
            fileUpload: r.fileUploadFile || null
          };
          if (r.formAnswerId) {
            var formAnswer: TransientFormAnswer = {
              formAnswerId: r.formAnswerId,
              writeIn: r.writeIn,
              subQuestionId: r.formSubQuestionId,
            };
            temp[matchedResponseIndex].formAnswers.push(formAnswer);
          }
        }
      });

      setTransientQuestionResponses(temp);

      // canIncreasePageIndex();
    }
  }, [surveyResponse]);

  // setup transient question responses
  useEffect(() => {
    const transientQuestionResponses: TransientQuestionResponse[] = [];
    let tempEndQuestions: any[] = [];

    survey?.formQuestions?.map((q) => {
      if (q.isEndQuestion) {
        tempEndQuestions.push({
          Id: q.id,
          DisplayOrder: q.displayOrder,
          PageIndex: q.pageIndex,
        });
      }

      transientQuestionResponses.push({
        questionId: q.id,
        writeIn: q.defaultAnswer || null,
        writeInDate: null,
        writeInDecimal: null,
        formAnswers: [],
        signatureFile: null,
        fileUpload: null,
      });
    });

    setTransientQuestionResponses(transientQuestionResponses);
  }, [survey]);

  useEffect(() => {
    if (firstErroredQuestion) document.getElementById(firstErroredQuestion)!.scrollIntoView(true);
  }, [firstErroredQuestion]);

  useEffect(() => {
    if (currentPage) {
      document.getElementById("topPage")!.scrollIntoView(true);
    }
  }, [pageIndex]);

  useEffect(() => {
    const erroredQuestions: number[] = [];
    // Get all errors
    for (const error in validationErrors) {
      const questionId: number = parseInt(error.replace("questionId", ""));
      erroredQuestions.push(questionId);
    }

    if (!erroredQuestions.length) return;

    let firstErroredQuestionId: number | null = null;

    // find first error's page
    surveyPages?.forEach((page, pageIndex) => {
      if (firstErroredQuestionId == null) {
        page.sections!.forEach((section) => {
          if (firstErroredQuestionId == null) {
            section.questions?.forEach((question) => {
              if (firstErroredQuestionId == null && erroredQuestions.includes(question.id)) {
                setCurrentPage(page);
                setPageIndex(pageIndex + 1);
                firstErroredQuestionId = question.id;
                setFirstErroredQuestion(`questionId${question.id}`);
              }
            });
          }
        });
      }
    });
  }, [validationErrors]);

  const updateTransientResponse = (questionResponse: TransientQuestionResponse) => {
    setTransientQuestionResponses(
      produce((draft) => {
        let draftQuestionResponseIndex = draft.findIndex((q) => q.questionId == questionResponse.questionId);

        draft[draftQuestionResponseIndex] = questionResponse;
      })
    );
  };

  const getSurvey = () => {
    ApiService.publicSurvey.getSurvey(params.id, params.client).then((response) => {
      setHasBranches(response.hasBranches);
      setSurvey(response.form);
      setSurveyPages(response.pages);
      setMaxPageIndex(response.pages?.length || 1);

      if (response.leftLogoBase64) {
        setLeftLogoUrl(response.leftLogoBase64);
      }
      if (response.centerLogoBase64) {
        setCenterLogoUrl(response.centerLogoBase64);
      }
      if (response.rightLogoBase64) {
        setRightLogoUrl(response.rightLogoBase64);
      }
    });
  };

  const fetchViewResource = () => {
    ApiService.publicSurvey.getSurveyViewResource(params.id, params.client).then((response) => {
      setViewResource(response);
    });
  };

  const submitResponse = () => {
    if (submissionInProgressRef.current) {
      return;
    }

    submissionInProgressRef.current = true;
    setIsSubmitting(true);

    // convert transient -> form response
    const formQuestionResponses: FormQuestionResponse[] = [];

    transientQuestionResponses.map((transient) => {
      // only add in transient responses that have an actual answer
      if (responseHasAnswer(transient) && responseIsActive(transient)) {
        // if form question has answers (i.e. multiple choice, matrix, etx)
        if (transient.formAnswers.length > 0) {
          transient.formAnswers.forEach((answer) => {
            const questionResponse: FormQuestionResponse = createEntity<FormQuestionResponse>({
              formQuestionId: transient.questionId,
              formAnswerId: answer.formAnswerId,
              formSubQuestionId: answer.subQuestionId,
              writeIn: answer.writeIn,
            });
            formQuestionResponses.push(questionResponse);
          });
        } else {
          const questionResponse: FormQuestionResponse = createEntity<FormQuestionResponse>({
            formQuestionId: transient.questionId,
            writeIn: transient.writeIn,
            writeInDate: transient.writeInDate,
            signatureUploadData: transient.signatureFile,
            fileUploadFile: transient.fileUpload
          });


          formQuestionResponses.push(questionResponse);
        }
      }
    });

    let formResponse: PublicSurveyResponseDto = {
      formQuestionResponses: formQuestionResponses,
      formGuid: params.id,
    };

    ApiService.publicSurvey
      .saveFormResponse(formResponse, params.client)
      .then((response) => {
        setShowSubmissionPage(true);
        dispatch(setAppToast(newToast("success")));
      })
      .catch((ex) => {
        submissionInProgressRef.current = false;
        setIsSubmitting(false);
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.data);
      });
  };

  const increasePageIndex = (currentPageNumber: number) => {
    let nextPageNumber = currentPageNumber + 1;
    let nextPage = surveyPages!.find(p => p.pageNumber == nextPageNumber);

    if (nextPage) {
      setCurrentPage(nextPage);
    }
    setPageIndex(nextPageNumber);
  };

  const decreasePageIndex = (currentPageNumber: number) => {
    let prevPageNumber = currentPageNumber - 1;
    let prevPage = surveyPages!.find(p => p.pageNumber == prevPageNumber);

    if (prevPage) {
      setCurrentPage(prevPage);
    }
    setPageIndex(prevPageNumber);
  };

  const prepPrint = async () => {
    const temp : PublicSurveyPageDto = {
      pageNumber: currentPage?.pageNumber || 1,
      sections: surveyPages?.flatMap(p => p.sections) || []
    };
    await setCurrentPage(temp);
    await setIsPrinting(true);
  }

  const finishPrint = async () => {
    reactToPrintFn();
    const temp : PublicSurveyPageDto = {
      pageNumber: currentPage?.pageNumber || 1,
      sections: surveyPages?.find(p => p.pageNumber == pageIndex)?.sections || []
    };
    await setCurrentPage(temp);
    await setIsPrinting(false);
  }

  return (
    <Page>
      {optionalParams.isViewMode && (<Box position="relative" top={20} textAlign="center">
        <Button variant="contained" onClick={() => prepPrint()}>
          {isPrinting ? "Loading..." : "Print"}
        </Button>
      </Box>)}
      {survey && (
        <div ref={contentRef}>
          <Grid id={`topPage`} container spacing="lg" style={{ paddingTop: "90px" }}>
            {survey.formModeId == 1 && <FloatingHeader text="Draft" />}
            <Grid item md={4} xs={4} style={{ display: "flex", justifyContent: "center", padding: 0 }}>
              {leftLogoUrl && <img src={leftLogoUrl} style={{ maxWidth: "100%", maxHeight: 250 }} />}
            </Grid>
            <Grid item md={4} xs={4} style={{ display: "flex", justifyContent: "center", padding: 0 }}>
              {centerLogoUrl && <img src={centerLogoUrl} style={{ maxWidth: "100%", maxHeight: 250 }} />}
            </Grid>
            <Grid item md={4} xs={4} style={{ display: "flex", justifyContent: "center", padding: 0 }}>
              {rightLogoUrl && <img src={rightLogoUrl} style={{ maxWidth: "100%", maxHeight: 250 }} />}
            </Grid>

            <GridBreak />
            <Grid item xs={12}>
              <Stack alignItems="center" justifyContent="center" height="100%">
                <Box sx={{ maxWidth: "1000px", width: "100%", padding: isPrinting ? "30px" : "inherited" }} >
                  {survey && <PublicSurveyTitle showSubmissionMessage={showSubmittionPage} survey={survey} />}
                  {showSubmittionPage && <PublicSurveySubmission survey={survey} />}
                  {survey && currentPage && !showSubmittionPage && (
                    <PublicSurveyQuestionPage
                      page={currentPage}
                      survey={survey}
                      isPrinting={isPrinting}
                      printCallBack={finishPrint}
                      isViewMode={optionalParams.isViewMode || false}
                      transientQuestionResponses={transientQuestionResponses}
                      surveyResponse={surveyResponse}
                      currentPageIndex={pageIndex}
                      maxPageIndex={maxPageIndex}
                      increasePageIndex={increasePageIndex}
                      decreasePageIndex={decreasePageIndex}
                      updateQuestionResponse={updateTransientResponse}
                      submitResponse={submitResponse}
                      isSubmitting={isSubmitting}
                      validationErrors={validationErrors}
                    />
                  )}
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </div>
      )}
      <Box position="relative" bottom={20} zIndex={100} textAlign="center">
        <Typography variant="caption">
          <>Copyright © 2006-{new Date().getFullYear()} Cobro Consulting. All Rights Reserved.</>
        </Typography>
      </Box>
    </Page>
  );
};
